import feathersClient from "../../feathers-client";

const defaultStaffUserPagination = {
	$skip: 0,
	$limit: 20,
};

const defaultStaffListPagination = {
	$skip: 0,
	$limit: 10,
};

const buildDefaultStaffUserFilter = (rootGetters) => [
	{ key: "roleId", value: rootGetters.currentUserApprovedRoleId },
	{ key: "staffTypeName", value: null },
	{
		key: "careLocationId",
		value: rootGetters.currentUserApprovedCareLocationId,
	},
	{ key: "employmentNames", value: rootGetters.allEmploymentNames }, // all selected
	{ key: "shiftPersonaId", value: null }, // nothing until the API responds
	{ key: "skillMatchBandId", value: null },
	{ key: "hardToFillOnly", value: true },
];

const emptyReportFilterSelections = () => ({
	administrativeGroupIds: [],
	careLocationIds: [],
	roleIds: [],
	employmentNames: [],
	shiftPersonaIds: [],
	skillMatchBandIds: [],
});

// Every option the filter panel offers, as [{ id, name }], so the drill-down pages can
// list the same choices instead of only the ones their own rows happen to mention
const emptyReportFilterOptions = () => ({
	administrativeGroups: [],
	careLocations: [],
	roleTypes: [],
	employmentTypes: [],
});

// Removing a chip strips its ids out of the query, whichever key carried them. A key left
// empty is dropped: an absent key means "not filtered", an empty one filters on nothing.
const queryWithoutValues = (reportFilterQuery, values) =>
	Object.fromEntries(
		Object.entries(reportFilterQuery)
			.map(([key, value]) => {
				if (Array.isArray(value))
					return [
						key,
						value.filter((entry) => !values.includes(entry)),
					];
				return [key, values.includes(value) ? null : value];
			})
			.filter(([, value]) =>
				Array.isArray(value) ? value.length : value != null
			)
	);

export default {
	state: {
		staffUserList: [],
		staffUserFilter: [],
		staffUserPagination: defaultStaffUserPagination,
		staffListPagination: defaultStaffListPagination,
		shiftPersonaList: [], // [{ id, name, ..., selected: bool }]
		// [{ filterName: 'careLocation', filterId, filterLabel, queryValues: [ids] }]
		appliedReportFilters: [],
		reportFilterQuery: {},
		reportFilterSelections: emptyReportFilterSelections(),
		reportFilterOptions: emptyReportFilterOptions(),
	},
	getters: {
		staffUserPage: (state) => {
			const { $limit, $skip } = state.staffUserPagination;
			return $skip / $limit + 1;
		},
		staffListPagination: (state) => state.staffListPagination,
		staffListPage: (state) => {
			const { $limit, $skip } = state.staffListPagination;
			return $skip / $limit + 1;
		},
		appliedReportFilters: (state) => state.appliedReportFilters,
		appliedReportFilterCount: (state) => state.appliedReportFilters.length,
		reportFilterQuery: (state) => state.reportFilterQuery,
		reportFilterSelections: (state) => state.reportFilterSelections,
		reportFilterOptions: (state) => state.reportFilterOptions,
	},
	mutations: {
		SET_STAFF_LIST: (state, payload) => {
			state.staffUserList = payload;
		},
		SET_REPORT_FILTERS: (
			state,
			{
				appliedReportFilters,
				reportFilterQuery,
				reportFilterSelections,
				reportFilterOptions,
			}
		) => {
			state.appliedReportFilters = appliedReportFilters;
			state.reportFilterQuery = reportFilterQuery;
			state.reportFilterSelections = reportFilterSelections;
			if (reportFilterOptions)
				state.reportFilterOptions = reportFilterOptions;
		},
		REMOVE_REPORT_FILTER: (state, { filterName, filterId }) => {
			const removed = state.appliedReportFilters.find(
				(filter) =>
					filter.filterName === filterName &&
					filter.filterId === filterId
			);
			if (!removed) return;
			state.appliedReportFilters = state.appliedReportFilters.filter(
				(filter) => filter !== removed
			);
			state.reportFilterQuery = queryWithoutValues(
				state.reportFilterQuery,
				removed.queryValues
			);
			state.reportFilterSelections = Object.fromEntries(
				Object.entries(state.reportFilterSelections).map(
					([key, ids]) => [key, ids.filter((id) => id !== filterId)]
				)
			);
		},
		CLEAR_REPORT_FILTERS: (state) => {
			state.appliedReportFilters = [];
			state.reportFilterQuery = {};
			state.reportFilterSelections = emptyReportFilterSelections();
		},
		SET_STAFF_USER_FILTER: (state, filter) => {
			state.staffUserFilter = filter;
		},
		SET_DEFAULT_STAFF_USER_FILTER: (state, filter) => {
			state.staffUserFilter = filter;
		},
		SET_STAFF_USER_PAGINATION: (state, data) => {
			state.staffUserPagination = {
				...state.staffUserPagination,
				...data,
			};
		},
		SET_STAFF_LIST_PAGINATION: (state, data) => {
			state.staffListPagination = {
				...state.staffListPagination,
				...data,
			};
		},
		SET_SHIFT_PERSONA_LIST: (state, payload) => {
			state.shiftPersonaList = payload;
		},
		// updates a single { key, value } entry inside staffUserFilter in place
		UPDATE_STAFF_USER_FILTER_VALUE: (state, { key, value }) => {
			state.staffUserFilter = state.staffUserFilter.map((entry) =>
				entry.key === key ? { ...entry, value } : entry
			);
		},
	},
	actions: {
		initDefaultStaffUserFilter: ({ rootGetters, commit }) => {
			commit(
				"SET_DEFAULT_STAFF_USER_FILTER",
				buildDefaultStaffUserFilter(rootGetters)
			);
		},

		// The table pages in the browser over the rows already fetched, so this only moves
		// $skip - nothing is refetched. Page numbers are 1-based, as the footer shows them.
		setStaffListPage: ({ commit, state }, page) => {
			const $skip = (page - 1) * state.staffListPagination.$limit;
			commit("SET_STAFF_LIST_PAGINATION", { $skip });
		},
		resetStaffListPage: ({ commit }) => {
			commit("SET_STAFF_LIST_PAGINATION", { $skip: 0 });
		},

		setReportFilters: ({ commit }, payload) => {
			commit("SET_REPORT_FILTERS", payload);
		},
		removeReportFilter: ({ commit }, filter) => {
			commit("REMOVE_REPORT_FILTER", filter);
		},
		clearReportFilters: ({ commit }) => {
			commit("CLEAR_REPORT_FILTERS");
		},

		loadSkillsVisualizationData: async (
			{ rootGetters, commit },
			filter
		) => {
			const url = "api/v3/skills-visualisation";

			const results = await feathersClient.service(url).find({
				query: { $role: rootGetters.currentUserRole, ...filter },
			});

			// results.shiftPersona = { selected: personaId|null, available: [{ personaId, personaName, hardToFillRank }, ...] }
			const { shiftPersona } = results;
			if (shiftPersona) {
				const list = (shiftPersona.available || []).map((persona) => ({
					...persona,
					selected: persona.personaId === shiftPersona.selected,
				}));

				commit("SET_SHIFT_PERSONA_LIST", list);
				commit("UPDATE_STAFF_USER_FILTER_VALUE", {
					key: "shiftPersonaId",
					value: shiftPersona.selected,
				});
			}

			return results;
		},
		
		loadCapabilityData: async ({ rootGetters, commit }, filter) => {
			const url = "api/v3/skills-visualisation";

			const results = await feathersClient.service(url).find({
				query: { $role: rootGetters.currentUserRole, ...filter },
			});
			
			// results.shiftPersona = { selected: personaId|null, available: [{ personaId, personaName, hardToFillRank }, ...] }

			return results;
		},

		loadStaffList: async ({ rootGetters, commit }, filter) => {
			const url = "api/v3/skills-visualisation";

			const results = await feathersClient.service(url).find({
				query: { $role: rootGetters.currentUserRole, ...filter },
			});
			
			// results.shiftPersona = { selected: personaId|null, available: [{ personaId, personaName, hardToFillRank }, ...] }

			return results;
		},
	},
};
